import React from 'react'

import { StaticImage } from 'gatsby-plugin-image'
import styled from 'styled-components'

import Footer from 'src/modules/organisms/footer/Footer'
import { GuidedBlocks } from 'src/components/blocks/GuidedBlocks'
import { LogostripSection } from 'src/components/blocks/Logostrip'
import Navbar from 'src/modules/organisms/navbar/Navbar'
import { ParkingLot } from 'src/components/blocks/ParkingLot'
import { QuoteCardNew } from 'src/components/blocks/QuoteCard'
import SubNav from 'src/components/blocks/SubNav'
import { Body2, TitleH2 } from 'src/components/blocks/Typography'
import { WayFinder } from 'src/components/blocks/WayFinder'
import { Container } from 'src/components/partials/Container'
import FaIcon from 'src/components/partials/FaIcon'
import { Column, Grid } from 'src/components/partials/Grid'
import Layout from 'src/components/partials/Layout'
import MediaObject from 'src/components/partials/MediaObject'
import { ResponsiveSectionWrapper } from 'src/components/partials/Section'
import SectionHeader from 'src/components/partials/SectionHeader'
import SEO from 'src/components/partials/Seo'
import UIList from 'src/components/partials/UIList'
import { sniderClickupQuote } from 'src/data/quotes'
import {
  wayfinderDataMapping,
  wayfinderDataProjects,
  wayfinderDataWorkbooks
} from 'src/data/wayfinderData'
import { Breakpoints } from 'src/utilities/Breakpoints'
import { BrandColors } from 'src/utilities/Colors'

const HeroSectionWrapper = styled(ResponsiveSectionWrapper)``

const ImgContainer = styled.div`
  margin-inline: auto;
  max-width: 1100px;
  margin-top: 3rem;

  @media (min-width: ${Breakpoints.md}px) {
    margin-top: 6rem;
  }
`

const HeroSection = () => (
  <HeroSectionWrapper paddingLarge='6rem 0 6rem' paddingSmall='6rem 0 2rem'>
    <Container>
      <SectionHeader
        titleContent='Embed a data importer in your app'
        titleAs='h1'
        titleMaxWidth='65ch'
        topHatText='Portal'
        bodyMaxWidth='100ch'
        bodyContent='The elegant data importer that implements in hours, not weeks. Give your users the import experience you always dreamed of but never had time to build.'
        primaryCTA={{
          label: 'Get a demo',
          path: '/demo/portal/'
        }}
      />

      <ImgContainer>
        <StaticImage
          alt=''
          src='../../../../static/img/platform/portal/portal-hero@2x.webp'
          width={2200}
        />
      </ImgContainer>
    </Container>
  </HeroSectionWrapper>
)

const TheNewStandardWrapper = styled(ResponsiveSectionWrapper)``

const TheNewStandard = () => (
  <TheNewStandardWrapper paddingLarge='6.25rem 0' paddingSmall='4rem 0'>
    <Container>
      <SectionHeader
        titleContent='The new standard for data import'
        titleAs='h2'
        bodyContent='Provide a simple and efficient data import option for users.'
        titleMaxWidth='100ch'
        sectionMaxWidth='100ch'
      />
    </Container>

    <Grid
      container
      columns={{
        md: 2
      }}
      spacing={{
        xs: 2,
        lg: 4
      }}
    >
      <Column>
        <UIList gap='2.5rem' style={{ maxWidth: '45ch' }}>
          <li>
            <MediaObject
              content='Flatfile’s JavaScript configurator allows you to set a target model for data validation, allowing users to match incoming file data.'
              image={
                <FaIcon classes='fa-sharp fa-light fa-search' size='1.5rem' />
              }
              imageBgColor={BrandColors.pigeon200}
              title='Ingest'
              titleAs='h3'
            />
          </li>

          <li>
            <MediaObject
              content='Flatfile learns over time how data should be organized, saving time and making the process more efficient for your customers.'
              image={
                <FaIcon
                  classes='fa-sharp fa-light fa-table-tree'
                  size='1.5rem'
                />
              }
              imageBgColor={BrandColors.pigeon200}
              title='Structure'
              titleAs='h3'
            />
          </li>

          <li>
            <MediaObject
              content="Flatfile's validation features give you control over how data is formatted. Ensure imported data is clean and ready to use."
              image={
                <FaIcon
                  classes='fa-sharp fa-light fa-input-numeric'
                  size='1.5rem'
                />
              }
              imageBgColor={BrandColors.pigeon200}
              title='Validate'
              titleAs='h3'
            />
          </li>
        </UIList>
      </Column>
      <Column>
        <StaticImage
          alt=''
          className='u-solidShadow'
          src='../../../../static/img/platform/portal/ingest@2x.webp'
          width={1158}
        />
      </Column>
    </Grid>
  </TheNewStandardWrapper>
)

const ParkingLotTier2Data = [
  {
    title: 'Internationalization',
    body: "The Flatfile importer automatically translates to your customer's chosen system language."
  },
  {
    title: 'Data hooks',
    body: 'Advanced functions for inline data validation and transformation.'
  },
  {
    title: 'Upload CSV, XLS, or paste data',
    body: "Allow data to be uploaded using a CSV, XLS, or even manual pasting from the user's clipboard."
  },
  {
    title: 'AI-assisted imports',
    body: 'Automatically match 95% of imported columns using machine learning and fuzzy matching.'
  },
  {
    title: 'Webhooks',
    body: 'Pull data from Flatfile directly into your application database by setting up a webhook listener.'
  },
  {
    title: 'Customize styles',
    body: 'Personalize the Flatfile importer to match the look and feel of your app.'
  }
]

const faqData = [
  {
    question: 'What is the meaning of life?',
    answer: <Body2>No one knows.</Body2>
  },
  {
    question: 'What is the meaning of life?',
    answer: <Body2>No one knows.</Body2>
  },
  {
    question: 'What is the meaning of life?',
    answer: <Body2>No one knows.</Body2>
  }
]

const DevsInMindWrapper = styled(ResponsiveSectionWrapper)``

const DevsInMind = () => (
  <DevsInMindWrapper paddingLarge='6.25rem 0' paddingSmall='4rem 0'>
    <Grid
      container
      columns={{
        md: 2
      }}
      spacing={{
        xs: 2,
        lg: 8
      }}
      style={{ placeItems: 'center' }}
    >
      <Column>
        <TitleH2 marginBottom='1rem'>Built with developers in mind</TitleH2>
        <Body2>
          Flatfile’s API-first, event-driven architecture provides full control
          and flexibility for any business or data requirements without having
          to deal with the headaches of building it yourself.
        </Body2>
      </Column>
      <Column>
        <StaticImage
          alt=''
          src='../../../../static/img/platform/portal/portal-devs-in-mind@2x.webp'
          width={476}
        />
      </Column>
    </Grid>
  </DevsInMindWrapper>
)

const WayfinderData = [
  wayfinderDataMapping,
  wayfinderDataProjects,
  wayfinderDataWorkbooks
]

const PortalPage = () => {
  return (
    <Layout>
      <Navbar />
      <SubNav />
      <HeroSection />
      <LogostripSection
        showTopHat
        paddingLarge='2rem 0'
        paddingSmall='2rem 0'
      />
      <TheNewStandard />
      <QuoteCardNew quote={sniderClickupQuote} />
      <ParkingLot
        sectionHeader={{
          titleContent: 'Data that works for you',
          titleAs: 'h2',
          bodyContent: 'Ensure imported data is clean and ready to use.'
        }}
        tier2Data={ParkingLotTier2Data}
      />
      <DevsInMind />
      <GuidedBlocks
        theme='dark'
        headerData={{
          title: 'Getting started is easy',
          body: 'Explore the Flatfile Data Exchange Platform and build the ideal data file import experience for your users - faster, easier and safer.',
          topHat: ''
        }}
        cardData={[
          {
            body: 'Add an elegant import button to your web app.',
            linkLabel: 'Get started with Portal for free',
            linkPath: 'sign-up/',
            title: 'Start'
          },
          {
            body: 'Get a custom sales demo for your use case',
            linkLabel: 'Contact us',
            linkPath: '/demo/',
            title: 'Demo'
          }
        ]}
      />
      {/* <FaqSection
        sectionHeaderBody='Everything you need to know about Portal'
        faqData={faqData}
      /> */}
      <WayFinder cardData={WayfinderData} />

      <Footer />
    </Layout>
  )
}

export default PortalPage

export const Head = () => (
  <SEO
    description='Give users the import experience you always dreamed of but never had time to build. Support any data or workflow requirements without the headaches.'
    title='Easily add a powerful CSV importer to your app'
  />
)
